import {
  LeadAnalysisKPI,
  PeerComparisonResult,
  TAB_CONFIG,
  TabId
} from "@/components/layouts/LeadAnalysis/types";
import {
  ChartDataType,
  LeadAnalysisData,
  MonthlyMetric,
  SettingsPeriod
} from "@/entities/reporting/types";
import {
  DreamerStackBarChartDataType,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";
import { segmentColorAt } from "@/components/DreamerCharts/chartColors";
import { getCurrentLocale } from "@/utils/locale";
import { reducePrecision } from "@/utils/numbers";
import { parseISO } from "date-fns";
import { TFunction } from "i18next";
import { useMemo } from "react";

export function extractMetricValues(
  data: any,
  metricPath: string,
  valueField = "you"
) {
  const pathParts = metricPath.split(".");
  let currentObj = data;
  for (const part of pathParts) {
    if (!currentObj[part]) {
      console.error(`Path ${part} not found in data`);
      return [];
    }
    currentObj = currentObj[part];
  }

  const byMonths = currentObj.byMonths;
  if (!byMonths) {
    console.error("byMonths data not found");
    return [];
  }

  const monthKeys = Object.keys(byMonths).sort();
  return monthKeys.map((month) => {
    const monthData = byMonths[month];
    return { value: monthData[valueField] || 0 };
  });
}

export function useCommonMetrics(data: LeadAnalysisData | null | undefined) {
  const avgLeadsData = extractMetricValues(
    data,
    "leadsAnalysis.avgMonthlyQualifiedConvertedLeads"
  );
  const qualificationData = extractMetricValues(
    data,
    "leadsAnalysis.leadQualificationRate"
  );
  const conversionData = extractMetricValues(
    data,
    "leadsAnalysis.leadConversionRate"
  );

  return {
    avgLeadsData,
    qualificationData,
    conversionData
  };
}

export const KPI_TRANSLATIONS = {
  [LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads]: {
    default: "leadAnalysis.kpi.newLeads",
    peer: "leadAnalysis.kpi.avgNewLeads"
  },
  [LeadAnalysisKPI.LeadQualificationRate]: "leadAnalysis.kpi.leadQualification",
  [LeadAnalysisKPI.LeadConversionRate]: "leadAnalysis.kpi.leadConversion"
};

export const PERIOD_TRANSLATIONS: Partial<Record<SettingsPeriod, string>> = {
  [SettingsPeriod.YTD]: "leadAnalysis.time.ytd",
  [SettingsPeriod.LAST_12_MONTHS]: "leadAnalysis.time.last12Month",
  [SettingsPeriod.LAST_24_MONTHS]: "leadAnalysis.time.last24Month",
  [SettingsPeriod.ALL_TIMES]: ""
};

export const kpiToTab = (kpi: LeadAnalysisKPI): TabId | undefined => {
  const entry = Object.entries(TAB_CONFIG).find(
    ([_, config]) => config.kpi === kpi
  );
  return entry ? (entry[0] as TabId) : undefined;
};

export const getKpiTitle = (
  kpi: LeadAnalysisKPI,
  t: TFunction<"translation", undefined>,
  isPeer?: boolean
): string => {
  const translationKey = KPI_TRANSLATIONS[kpi];

  if (typeof translationKey === "object") {
    return isPeer && translationKey.peer
      ? t(translationKey.peer)
      : t(translationKey.default);
  }

  return t(translationKey);
};

export const getPeriodText = (
  period: SettingsPeriod,
  t: TFunction<"translation", undefined>
): string => {
  const key = PERIOD_TRANSLATIONS[period];
  return key ? t(key) : "";
};

export const getTabOptions = (
  t: TFunction<"translation", undefined>
): Record<TabId, string> => {
  return Object.fromEntries(
    Object.entries(TAB_CONFIG).map(([id, config]) => [
      id,
      id === "avgNewLeads"
        ? `${t("leadAnalysis.kpi.newLeads")} ${t("leadAnalysis.time.byMonth")}`
        : `${t(config.translationKey)} ${t("leadAnalysis.time.byMonth")}`
    ])
  ) as Record<TabId, string>;
};

export const getDataByKPI = (
  kpiOrTabId: LeadAnalysisKPI | TabId,
  data: any
): any => {
  const kpi =
    kpiOrTabId in TAB_CONFIG
      ? TAB_CONFIG[kpiOrTabId as TabId].kpi
      : (kpiOrTabId as LeadAnalysisKPI);

  return data?.[kpi];
};

const formatMonthLabel = (date: string) =>
  parseISO(date).toLocaleDateString(getCurrentLocale(), {
    month: "2-digit",
    year: "2-digit"
  });

export function useLeadChartData(
  data: LeadAnalysisData | null | undefined,
  kpiOrTabId: LeadAnalysisKPI | TabId
): ChartDataType[] {
  return useMemo(() => {
    if (!data || !data.leadsAnalysis) return [];

    const kpi = kpiOrTabId as LeadAnalysisKPI;

    const kpiData = data.leadsAnalysis[kpi];
    if (!kpiData || !kpiData.byMonths) return [];

    return Object.entries(kpiData.byMonths).map(
      ([date, values]: [string, MonthlyMetric]) => ({
        date: formatMonthLabel(date),
        youValue: reducePrecision(values?.you || 0),
        teamValue: reducePrecision(values?.team || 0),
        organizationValue: reducePrecision(values?.org || 0)
      })
    );
  }, [data, kpiOrTabId]);
}

export function useAllLeadChartData(data: LeadAnalysisData | null | undefined) {
  const avgMonthlyNewLeadsByMonth = useLeadChartData(
    data,
    LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads
  );
  const leadQualificationRateByMonth = useLeadChartData(
    data,
    LeadAnalysisKPI.LeadQualificationRate
  );
  const leadConversionRateByMonth = useLeadChartData(
    data,
    LeadAnalysisKPI.LeadConversionRate
  );

  return {
    avgMonthlyNewLeadsByMonth,
    leadQualificationRateByMonth,
    leadConversionRateByMonth
  };
}

export function useLeadSourceChartData(
  data: LeadAnalysisData | null | undefined,
  kpi: LeadAnalysisKPI,
  noSourceLabel: string
): { chartData: DreamerStackBarChartDataType[]; segments: StackSegmentConfig[] } {
  return useMemo(() => {
    const kpiData = data?.leadsAnalysis?.[kpi];
    if (!kpiData?.bySource?.length || !kpiData.byMonths) {
      return { chartData: [], segments: [] };
    }

    const months = Object.keys(kpiData.byMonths);
    // Sorted by name with no source last, and colored before filtering, so a source keeps its
    // color on every KPI chart
    const sources = [...kpiData.bySource]
      .sort((a, b) =>
        a.source === null
          ? 1
          : b.source === null
            ? -1
            : a.source.localeCompare(b.source)
      )
      .map((series, index) => ({
        // Index-based keys: recharts reads a dotted dataKey as a path, and source names are free text
        dataKey: `source${index}`,
        label: series.source ?? noSourceLabel,
        color: segmentColorAt(index),
        byMonths: series.byMonths
      }))
      // A source with no leads adds nothing to the stack; a 0% rate is still a value to plot
      .filter(
        (series) =>
          kpi !== LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads ||
          months.some((month) => (series.byMonths[month] || 0) > 0)
      );

    const segments = sources.map(({ dataKey, label, color }) => ({
      dataKey,
      label,
      color
    }));

    const chartData = months.map((month) => {
      const row: DreamerStackBarChartDataType = {
        label: formatMonthLabel(month)
      };
      sources.forEach((series) => {
        row[series.dataKey] = series.byMonths[month] || 0;
      });
      return row;
    });

    return { chartData, segments };
  }, [data, kpi, noSourceLabel]);
}

export const extractPeerComparison = (
  data: LeadAnalysisData,
  kpi: LeadAnalysisKPI
): PeerComparisonResult => {
  if (
    !data.leadsAnalysis ||
    !data.leadsAnalysis[kpi] ||
    !data.leadsAnalysis[kpi].overall
  ) {
    return {
      you: { value: 0 },
      team: { value: 0 },
      org: { value: 0 },
      industry: { value: 0 }
    };
  }

  const overallData = data.leadsAnalysis[kpi].overall;

  return {
    you: {
      value: overallData.value
    },
    team: {
      value: overallData.team
    },
    org: {
      value: overallData.org
    },
    industry: {
      value: overallData.industry || 0
    }
  };
};
